import { useState, useEffect, useRef, useCallback } from 'react';
import { getGlobalConfig } from '../utils/getGlobalConfig';

export const useGooglePlacesPredictions = () => {
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const { googleApiKey } = getGlobalConfig();

  const fetchPredictions = async (inputValue: string) => {
    if (!inputValue.trim() || !autocompleteService.current) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    try {
      const request: google.maps.places.AutocompletionRequest = {
        input: inputValue,
        types: ['geocode', 'establishment']
      };

      autocompleteService.current.getPlacePredictions(
        request,
        (
          results: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          setLoading(false);
          const isOk = status === google.maps.places.PlacesServiceStatus.OK && results;
          setPredictions(isOk ? results : []);
        }
      );
    } catch (error) {
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleMapsScript = useCallback(() => {
    if (window.google && window.google.maps) {
      setScriptLoaded(true);
      autocompleteService.current = new google.maps.places.AutocompleteService();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setScriptLoaded(true);
      autocompleteService.current = new google.maps.places.AutocompleteService();
    };
    document.head.appendChild(script);
  }, [googleApiKey]);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  return {
    predictions,
    loading,
    scriptLoaded,
    fetchPredictions
  };
};
